<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import BotPersonaTemplates from './BotPersonaTemplates.vue';

const props = defineProps({
  persona: { type: Object, default: null },
});
const emit = defineEmits(['saved', 'close']);
const { t } = useI18n();
const store = useStore();

const TABS = [
  'IDENTITY',
  'VOICE',
  'MESSAGES',
  'BEHAVIOR',
  'EMOTIONAL',
  'CULTURAL',
  'RULES',
  'AI_CONFIG',
  'KNOWLEDGE',
];

const TAB_ICONS = {
  IDENTITY: 'i-lucide-user-circle',
  VOICE: 'i-lucide-mic',
  MESSAGES: 'i-lucide-message-square',
  BEHAVIOR: 'i-lucide-settings-2',
  EMOTIONAL: 'i-lucide-heart',
  CULTURAL: 'i-lucide-globe',
  RULES: 'i-lucide-shield-check',
  AI_CONFIG: 'i-lucide-cpu',
  KNOWLEDGE: 'i-lucide-book-open',
};

const tabLabels = computed(() => ({
  IDENTITY: t('MINISTRY.BOT_PERSONAS.TABS.IDENTITY'),
  VOICE: t('MINISTRY.BOT_PERSONAS.TABS.VOICE'),
  MESSAGES: t('MINISTRY.BOT_PERSONAS.TABS.MESSAGES'),
  BEHAVIOR: t('MINISTRY.BOT_PERSONAS.TABS.BEHAVIOR'),
  EMOTIONAL: t('MINISTRY.BOT_PERSONAS.TABS.EMOTIONAL'),
  CULTURAL: t('MINISTRY.BOT_PERSONAS.TABS.CULTURAL'),
  RULES: t('MINISTRY.BOT_PERSONAS.TABS.RULES'),
  AI_CONFIG: t('MINISTRY.BOT_PERSONAS.TABS.AI_CONFIG'),
  KNOWLEDGE: t('MINISTRY.BOT_PERSONAS.TABS.KNOWLEDGE'),
}));

const TONES = [
  'friendly',
  'professional',
  'casual',
  'empathetic',
  'thoughtful',
  'warm',
  'enthusiastic',
  'gentle',
  'bold',
  'scholarly',
];
const toneLabels = computed(() => ({
  friendly: t('MINISTRY.BOT_PERSONAS.TONES.FRIENDLY'),
  professional: t('MINISTRY.BOT_PERSONAS.TONES.PROFESSIONAL'),
  casual: t('MINISTRY.BOT_PERSONAS.TONES.CASUAL'),
  empathetic: t('MINISTRY.BOT_PERSONAS.TONES.EMPATHETIC'),
  thoughtful: t('MINISTRY.BOT_PERSONAS.TONES.THOUGHTFUL'),
  warm: t('MINISTRY.BOT_PERSONAS.TONES.WARM'),
  enthusiastic: t('MINISTRY.BOT_PERSONAS.TONES.ENTHUSIASTIC'),
  gentle: t('MINISTRY.BOT_PERSONAS.TONES.GENTLE'),
  bold: t('MINISTRY.BOT_PERSONAS.TONES.BOLD'),
  scholarly: t('MINISTRY.BOT_PERSONAS.TONES.SCHOLARLY'),
}));

const VOCABULARY_LEVELS = ['simple', 'conversational', 'academic'];
const vocabLabels = computed(() => ({
  simple: t('MINISTRY.BOT_PERSONAS.VOCABULARY_LEVELS.SIMPLE'),
  conversational: t('MINISTRY.BOT_PERSONAS.VOCABULARY_LEVELS.CONVERSATIONAL'),
  academic: t('MINISTRY.BOT_PERSONAS.VOCABULARY_LEVELS.ACADEMIC'),
}));

const SENTENCE_STRUCTURES = ['short', 'mixed', 'long'];
const sentenceLabels = computed(() => ({
  short: t('MINISTRY.BOT_PERSONAS.SENTENCE_STRUCTURES.SHORT'),
  mixed: t('MINISTRY.BOT_PERSONAS.SENTENCE_STRUCTURES.MIXED'),
  long: t('MINISTRY.BOT_PERSONAS.SENTENCE_STRUCTURES.LONG'),
}));

const HUMOR_LEVELS = ['none', 'light', 'moderate'];
const humorLabels = computed(() => ({
  none: t('MINISTRY.BOT_PERSONAS.HUMOR_LEVELS.NONE'),
  light: t('MINISTRY.BOT_PERSONAS.HUMOR_LEVELS.LIGHT'),
  moderate: t('MINISTRY.BOT_PERSONAS.HUMOR_LEVELS.MODERATE'),
}));

const FORMALITY_LEVELS = ['informal', 'semi-formal', 'formal'];
const formalityLabels = computed(() => ({
  informal: t('MINISTRY.BOT_PERSONAS.FORMALITY_LEVELS.INFORMAL'),
  'semi-formal': t('MINISTRY.BOT_PERSONAS.FORMALITY_LEVELS.SEMI_FORMAL'),
  formal: t('MINISTRY.BOT_PERSONAS.FORMALITY_LEVELS.FORMAL'),
}));

const EMOJI_FREQUENCIES = ['none', 'occasional', 'moderate'];
const emojiFreqLabels = computed(() => ({
  none: t('MINISTRY.BOT_PERSONAS.EMOJI_FREQUENCIES.NONE'),
  occasional: t('MINISTRY.BOT_PERSONAS.EMOJI_FREQUENCIES.OCCASIONAL'),
  moderate: t('MINISTRY.BOT_PERSONAS.EMOJI_FREQUENCIES.MODERATE'),
}));

const SCRIPTURE_STYLES = ['niv', 'esv', 'kjv', 'nasb', 'nlt'];
const scriptureLabels = computed(() => ({
  niv: t('MINISTRY.BOT_PERSONAS.SCRIPTURE_STYLES.NIV'),
  esv: t('MINISTRY.BOT_PERSONAS.SCRIPTURE_STYLES.ESV'),
  kjv: t('MINISTRY.BOT_PERSONAS.SCRIPTURE_STYLES.KJV'),
  nasb: t('MINISTRY.BOT_PERSONAS.SCRIPTURE_STYLES.NASB'),
  nlt: t('MINISTRY.BOT_PERSONAS.SCRIPTURE_STYLES.NLT'),
}));

const EMPATHY_LEVELS = ['low', 'medium', 'high'];
const empathyLabels = computed(() => ({
  low: t('MINISTRY.BOT_PERSONAS.EMPATHY_LEVELS.LOW'),
  medium: t('MINISTRY.BOT_PERSONAS.EMPATHY_LEVELS.MEDIUM'),
  high: t('MINISTRY.BOT_PERSONAS.EMPATHY_LEVELS.HIGH'),
}));

const JOY_RESPONSE_STYLES = ['celebratory', 'warm', 'encouraging'];
const joyLabels = computed(() => ({
  celebratory: t('MINISTRY.BOT_PERSONAS.JOY_RESPONSE_STYLES.CELEBRATORY'),
  warm: t('MINISTRY.BOT_PERSONAS.JOY_RESPONSE_STYLES.WARM'),
  encouraging: t('MINISTRY.BOT_PERSONAS.JOY_RESPONSE_STYLES.ENCOURAGING'),
}));

const DEFLECTION_STYLES = [
  'warm_redirect',
  'humor_redirect',
  'thoughtful_engagement',
  'gentle_firm',
];
const deflectionStyleLabels = computed(() => ({
  warm_redirect: t('MINISTRY.BOT_PERSONAS.DEFLECTION_STYLES.WARM_REDIRECT'),
  humor_redirect: t('MINISTRY.BOT_PERSONAS.DEFLECTION_STYLES.HUMOR_REDIRECT'),
  thoughtful_engagement: t(
    'MINISTRY.BOT_PERSONAS.DEFLECTION_STYLES.THOUGHTFUL_ENGAGEMENT'
  ),
  gentle_firm: t('MINISTRY.BOT_PERSONAS.DEFLECTION_STYLES.GENTLE_FIRM'),
}));

const RESPONSE_LENGTHS = ['short', 'medium', 'detailed'];
const INSTRUCTION_CATEGORIES = [
  'doctrine',
  'style',
  'sensitivity',
  'behavior',
  'cultural',
];
const EXPERTISE_LEVELS = ['beginner', 'intermediate', 'expert'];
const PHRASE_TYPES = [
  'greeting',
  'farewell',
  'encouragement',
  'prayer',
  'transition',
];

const activeTab = ref('IDENTITY');

function emptyForm() {
  return {
    name: '',
    tagline: '',
    tone: 'friendly',
    greeting: '',
    description: '',
    avatar_url: '',
    backstory: '',
    age_persona: '',
    gender_persona: '',
    origin_story: '',
    vocabulary_level: 'conversational',
    sentence_structure: 'mixed',
    humor_level: 'light',
    formality_level: 'semi-formal',
    use_contractions: true,
    use_emojis: false,
    emoji_frequency: 'none',
    scripture_style: 'niv',
    opening_message: '',
    fallback_message: '',
    handoff_message: '',
    handoff_signal: '#######',
    re_engagement_message: '',
    prayer_offer_message: '',
    human_handoff_message: '',
    response_length: 'medium',
    typing_speed_wpm: 40,
    min_reply_delay_secs: 1.0,
    max_reply_delay_secs: 4.0,
    enable_chunked_replies: false,
    max_chunks_per_reply: 3,
    max_conversation_turns: 0,
    auto_escalate_after_turns: null,
    empathy_level: 'medium',
    crisis_sensitivity: true,
    grief_protocol: '',
    doubt_protocol: '',
    anger_protocol: '',
    joy_response_style: 'celebratory',
    supported_languages: ['en'],
    auto_detect_language: true,
    cultural_sensitivity_notes: '',
    avoid_western_idioms: false,
    ministry_focus: [],
    forbidden_topics: [],
    required_disclaimers: [],
    always_affirm_before_correcting: true,
    never_debate_mode: false,
    deflection_style: 'warm_redirect',
    character_location: '',
    character_age: null,
    character_nationality: '',
    ministry_role: 'ministry team member',
    years_in_ministry: null,
    favourite_food: 'coffee and toast',
    morning_routine: 'early riser, coffee',
    hobby: 'reading',
    favourite_book: 'Mere Christianity',
    custom_deflections: [],
    alert_on_probe: true,
    system_prompt_override: '',
    temperature_override: null,
    model_override: '',
    config: {},
  };
}

const form = ref(emptyForm());

// Nested resources (editable lists)
const instructions = ref([]);
const expertises = ref([]);
const phrases = ref([]);
const kbEntries = ref([]);

// New row drafts
const newInstruction = ref({
  category: 'doctrine',
  instruction: '',
  priority: 0,
});
const newExpertise = ref({ topic: '', level: 'intermediate', notes: '' });
const newPhrase = ref({ phrase_type: 'greeting', phrase: '', language: 'en' });
const newKbEntry = ref({
  title: '',
  question: '',
  answer: '',
  category: 'general',
});

const KB_CATEGORIES = ['general', 'doctrine', 'faq', 'objection', 'testimony'];

watch(
  () => props.persona,
  p => {
    if (p) {
      form.value = {
        ...emptyForm(),
        name: p.name || '',
        tagline: p.tagline || '',
        tone: p.tone || 'friendly',
        greeting: p.greeting || '',
        description: p.description || '',
        avatar_url: p.avatar_url || '',
        backstory: p.backstory || '',
        age_persona: p.age_persona || '',
        gender_persona: p.gender_persona || '',
        origin_story: p.origin_story || '',
        vocabulary_level: p.vocabulary_level || 'conversational',
        sentence_structure: p.sentence_structure || 'mixed',
        humor_level: p.humor_level || 'light',
        formality_level: p.formality_level || 'semi-formal',
        use_contractions: p.use_contractions !== false,
        use_emojis: p.use_emojis === true,
        emoji_frequency: p.emoji_frequency || 'none',
        scripture_style: p.scripture_style || 'niv',
        opening_message: p.opening_message || '',
        fallback_message: p.fallback_message || '',
        handoff_message: p.handoff_message || '',
        handoff_signal: p.handoff_signal || '#######',
        re_engagement_message: p.re_engagement_message || '',
        prayer_offer_message: p.prayer_offer_message || '',
        human_handoff_message: p.human_handoff_message || '',
        response_length: p.response_length || 'medium',
        typing_speed_wpm: p.typing_speed_wpm || 40,
        min_reply_delay_secs: p.min_reply_delay_secs || 1.0,
        max_reply_delay_secs: p.max_reply_delay_secs || 4.0,
        enable_chunked_replies: p.enable_chunked_replies === true,
        max_chunks_per_reply: p.max_chunks_per_reply || 3,
        max_conversation_turns: p.max_conversation_turns || 20,
        auto_escalate_after_turns: p.auto_escalate_after_turns || null,
        empathy_level: p.empathy_level || 'medium',
        crisis_sensitivity: p.crisis_sensitivity !== false,
        grief_protocol: p.grief_protocol || '',
        doubt_protocol: p.doubt_protocol || '',
        anger_protocol: p.anger_protocol || '',
        joy_response_style: p.joy_response_style || 'celebratory',
        supported_languages: p.supported_languages || ['en'],
        auto_detect_language: p.auto_detect_language !== false,
        cultural_sensitivity_notes: p.cultural_sensitivity_notes || '',
        avoid_western_idioms: p.avoid_western_idioms === true,
        ministry_focus: p.ministry_focus || [],
        forbidden_topics: p.forbidden_topics || [],
        required_disclaimers: p.required_disclaimers || [],
        always_affirm_before_correcting:
          p.always_affirm_before_correcting !== false,
        never_debate_mode: p.never_debate_mode === true,
        deflection_style: p.deflection_style || 'warm_redirect',
        character_location: p.character_location || '',
        character_age: p.character_age || null,
        character_nationality: p.character_nationality || '',
        ministry_role: p.ministry_role || 'ministry team member',
        years_in_ministry: p.years_in_ministry || null,
        favourite_food: p.favourite_food || 'coffee and toast',
        morning_routine: p.morning_routine || 'early riser, coffee',
        hobby: p.hobby || 'reading',
        favourite_book: p.favourite_book || 'Mere Christianity',
        custom_deflections: p.custom_deflections || [],
        alert_on_probe: p.alert_on_probe !== false,
        system_prompt_override: p.system_prompt_override || '',
        temperature_override: p.temperature_override || null,
        model_override: p.model_override || '',
        config: p.config || {},
      };
      instructions.value = [...(p.persona_ai_instructions || [])];
      expertises.value = [...(p.persona_topic_expertises || [])];
      phrases.value = [...(p.persona_phrase_libraries || [])];
      kbEntries.value = [...(p.persona_kb_entries || [])];
    } else {
      form.value = emptyForm();
      instructions.value = [];
      expertises.value = [];
      phrases.value = [];
      kbEntries.value = [];
    }
  },
  { immediate: true }
);

function applyTemplate(tpl) {
  Object.keys(form.value).forEach(key => {
    if (tpl[key] !== undefined) form.value[key] = tpl[key];
  });
  form.value.name = tpl.name;
  form.value.tone = tpl.tone;
  form.value.greeting = tpl.greeting;
  if (tpl.initialInstructions?.length)
    instructions.value = tpl.initialInstructions.map(i => ({
      ...i,
      id: null,
    }));
  if (tpl.initialPhrases?.length)
    phrases.value = tpl.initialPhrases.map(p => ({ ...p, id: null }));
  if (tpl.initialKb?.length)
    kbEntries.value = tpl.initialKb.map(e => ({ ...e, id: null }));
}

const newCustomDeflection = ref('');

function addCustomDeflection() {
  const phrase = newCustomDeflection.value.trim();
  if (!phrase) return;
  form.value.custom_deflections = [
    ...(form.value.custom_deflections || []),
    phrase,
  ];
  newCustomDeflection.value = '';
}

function removeCustomDeflection(idx) {
  form.value.custom_deflections = form.value.custom_deflections.filter(
    (_, i) => i !== idx
  );
}

const isSaving = ref(false);

async function addInstruction() {
  if (!newInstruction.value.instruction.trim()) return;
  if (props.persona?.id) {
    const result = await store.dispatch('botPersonas/createInstruction', {
      personaId: props.persona.id,
      ...newInstruction.value,
    });
    if (result) instructions.value.push(result);
  } else {
    instructions.value.push({ ...newInstruction.value, id: null });
  }
  newInstruction.value = { category: 'doctrine', instruction: '', priority: 0 };
}

async function removeInstruction(idx) {
  const item = instructions.value[idx];
  if (item.id && props.persona?.id) {
    await store.dispatch('botPersonas/deleteInstruction', {
      personaId: props.persona.id,
      id: item.id,
    });
  }
  instructions.value.splice(idx, 1);
}

async function addExpertise() {
  if (!newExpertise.value.topic.trim()) return;
  if (props.persona?.id) {
    const result = await store.dispatch('botPersonas/createExpertise', {
      personaId: props.persona.id,
      ...newExpertise.value,
    });
    if (result) expertises.value.push(result);
  } else {
    expertises.value.push({ ...newExpertise.value, id: null });
  }
  newExpertise.value = { topic: '', level: 'intermediate', notes: '' };
}

async function removeExpertise(idx) {
  const item = expertises.value[idx];
  if (item.id && props.persona?.id) {
    await store.dispatch('botPersonas/deleteExpertise', {
      personaId: props.persona.id,
      id: item.id,
    });
  }
  expertises.value.splice(idx, 1);
}

async function addPhrase() {
  if (!newPhrase.value.phrase.trim()) return;
  if (props.persona?.id) {
    const result = await store.dispatch('botPersonas/createPhrase', {
      personaId: props.persona.id,
      ...newPhrase.value,
    });
    if (result) phrases.value.push(result);
  } else {
    phrases.value.push({ ...newPhrase.value, id: null });
  }
  newPhrase.value = { phrase_type: 'greeting', phrase: '', language: 'en' };
}

async function removePhrase(idx) {
  const item = phrases.value[idx];
  if (item.id && props.persona?.id) {
    await store.dispatch('botPersonas/deletePhrase', {
      personaId: props.persona.id,
      id: item.id,
    });
  }
  phrases.value.splice(idx, 1);
}

async function addKbEntry() {
  if (!newKbEntry.value.question.trim() || !newKbEntry.value.answer.trim())
    return;
  if (props.persona?.id) {
    const result = await store.dispatch('botPersonas/createKbEntry', {
      personaId: props.persona.id,
      ...newKbEntry.value,
    });
    if (result) kbEntries.value.push(result);
  } else {
    kbEntries.value.push({ ...newKbEntry.value, id: null });
  }
  newKbEntry.value = {
    title: '',
    question: '',
    answer: '',
    category: 'general',
  };
}

async function removeKbEntry(idx) {
  const item = kbEntries.value[idx];
  if (item.id && props.persona?.id) {
    await store.dispatch('botPersonas/deleteKbEntry', {
      personaId: props.persona.id,
      id: item.id,
    });
  }
  kbEntries.value.splice(idx, 1);
}

async function save() {
  if (!form.value.name.trim()) return;
  isSaving.value = true;
  try {
    let result;
    if (props.persona?.id) {
      result = await store.dispatch('botPersonas/update', {
        id: props.persona.id,
        ...form.value,
      });
    } else {
      result = await store.dispatch('botPersonas/create', form.value);
    }
    if (!result) return;
    useAlert(t('MINISTRY.BOT_PERSONAS.SAVE_SUCCESS'));
    emit('saved');
  } catch {
    // handled in store
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <div class="flex h-full min-h-0">
    <!-- ─── Left sidebar ──────────────────────────────── -->
    <aside
      class="w-56 bg-white border-r border-[#E8E6E0] flex flex-col flex-shrink-0"
    >
      <!-- Persona identity card -->
      <div class="px-4 pt-4 pb-3 border-b border-[#E8E6E0]">
        <div class="flex items-center gap-3">
          <div
            class="h-9 w-9 rounded-full bg-woot-100 flex items-center justify-center text-woot-600 font-bold text-sm flex-shrink-0 overflow-hidden"
          >
            <img
              v-if="form.avatar_url"
              :src="form.avatar_url"
              :alt="form.name"
              class="h-full w-full object-cover"
            />
            <span v-else>{{ form.name?.charAt(0)?.toUpperCase() || '?' }}</span>
          </div>
          <div class="min-w-0">
            <p
              class="text-sm font-semibold text-[#1A1A2E] truncate leading-tight"
            >
              {{ form.name || t('MINISTRY.BOT_PERSONAS.NEW_PERSONA') }}
            </p>
            <span
              class="text-xs px-1.5 py-0.5 rounded-full bg-woot-50 text-woot-600 capitalize"
              >{{ form.tone || 'friendly' }}</span
            >
          </div>
        </div>
      </div>

      <!-- Tab navigation -->
      <nav class="flex-1 overflow-y-auto py-2 px-2">
        <button
          v-for="tab in TABS"
          :key="tab"
          class="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-left mb-0.5"
          :class="
            activeTab === tab
              ? 'bg-woot-50 text-woot-700'
              : 'text-[#6B7280] hover:bg-[#F5F3EE] hover:text-[#1A1A2E]'
          "
          @click="activeTab = tab"
        >
          <span :class="TAB_ICONS[tab] + ' size-4 flex-shrink-0'" />
          {{ tabLabels[tab] }}
        </button>
      </nav>

      <!-- Compact live preview -->
      <div class="px-3 py-3 border-t border-[#E8E6E0]">
        <p
          class="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF] mb-2"
        >
          {{ t('MINISTRY.BOT_PERSONAS.LIVE_PREVIEW') }}
        </p>
        <div class="rounded-lg bg-[#F9F8F5] border border-[#E8E6E0] p-2.5">
          <div class="flex items-center gap-2 mb-2">
            <div
              class="h-6 w-6 rounded-full bg-woot-100 flex items-center justify-center text-woot-600 font-bold text-xs flex-shrink-0 overflow-hidden"
            >
              <img
                v-if="form.avatar_url"
                :src="form.avatar_url"
                class="h-full w-full object-cover"
              />
              <span v-else>{{
                form.name?.charAt(0)?.toUpperCase() || '?'
              }}</span>
            </div>
            <p class="text-xs font-semibold text-[#1A1A2E] truncate flex-1">
              {{ form.name || t('MINISTRY.BOT_PERSONAS.NEW_PERSONA') }}
            </p>
          </div>
          <div
            class="rounded-xl rounded-tl-none bg-white border border-[#E8E6E0] px-2.5 py-2"
          >
            <p class="text-xs text-[#374151] leading-relaxed line-clamp-3">
              {{
                form.greeting ||
                form.opening_message ||
                `Hi! I'm ${form.name || 'your ministry assistant'} — how can I help?`
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- Save / Cancel -->
      <div class="px-3 pb-4 flex flex-col gap-2">
        <button
          :disabled="!form.name?.trim() || isSaving"
          class="w-full px-4 py-2 rounded-lg bg-woot-500 text-white text-sm font-semibold hover:bg-woot-600 transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
          @click="save"
        >
          <span
            v-if="isSaving"
            class="i-lucide-loader-circle animate-spin size-4"
          />
          {{
            isSaving
              ? t('MINISTRY.BOT_PERSONAS.SAVING')
              : t('MINISTRY.BOT_PERSONAS.SAVE')
          }}
        </button>
        <button
          class="w-full px-4 py-2 rounded-lg border border-[#E8E6E0] text-sm text-[#6B7280] hover:bg-[#F5F3EE] transition-colors"
          @click="emit('close')"
        >
          {{ t('MINISTRY.BOT_PERSONAS.CANCEL') }}
        </button>
      </div>
    </aside>

    <!-- ─── Main content ──────────────────────────────── -->
    <div class="flex-1 overflow-y-auto min-w-0 bg-[#F9F8F5]">
      <div class="p-6 max-w-2xl flex flex-col gap-5">
        <!-- Template picker (new persona only) -->
        <BotPersonaTemplates v-if="!persona" @select="applyTemplate" />

        <!-- Tab heading -->
        <div class="flex items-center gap-3">
          <div
            class="h-8 w-8 rounded-lg bg-woot-100 flex items-center justify-center flex-shrink-0"
          >
            <span :class="TAB_ICONS[activeTab] + ' size-4 text-woot-600'" />
          </div>
          <h2 class="text-base font-bold text-[#1A1A2E]">
            {{ tabLabels[activeTab] }}
          </h2>
        </div>

        <!-- ── IDENTITY ──────────────────────────────── -->
        <template v-if="activeTab === 'IDENTITY'">
          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-user size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.BASIC_INFO') }}
            </h3>
            <div class="grid grid-cols-2 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.NAME')
                }}</label>
                <input
                  v-model="form.name"
                  type="text"
                  :placeholder="
                    t('MINISTRY.BOT_PERSONAS.FIELDS.NAME_PLACEHOLDER')
                  "
                  class="field-input"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.TAGLINE')
                }}</label>
                <input
                  v-model="form.tagline"
                  type="text"
                  :placeholder="
                    t('MINISTRY.BOT_PERSONAS.FIELDS.TAGLINE_PLACEHOLDER')
                  "
                  class="field-input"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.AVATAR_URL')
                }}</label>
                <input
                  v-model="form.avatar_url"
                  type="url"
                  :placeholder="
                    t('MINISTRY.BOT_PERSONAS.FIELDS.AVATAR_URL_PLACEHOLDER')
                  "
                  class="field-input"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.AGE_PERSONA')
                }}</label>
                <input
                  v-model="form.age_persona"
                  type="text"
                  :placeholder="
                    t('MINISTRY.BOT_PERSONAS.FIELDS.AGE_PERSONA_PLACEHOLDER')
                  "
                  class="field-input"
                />
              </div>
              <div class="col-span-2 flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.DESCRIPTION')
                }}</label>
                <textarea
                  v-model="form.description"
                  rows="2"
                  :placeholder="
                    t('MINISTRY.BOT_PERSONAS.FIELDS.DESCRIPTION_PLACEHOLDER')
                  "
                  class="field-input resize-none"
                />
              </div>
              <div class="col-span-2 flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.MINISTRY_FOCUS')
                }}</label>
                <input
                  v-model="form.ministry_focus_input"
                  type="text"
                  :placeholder="
                    t('MINISTRY.BOT_PERSONAS.FIELDS.MINISTRY_FOCUS_PLACEHOLDER')
                  "
                  class="field-input"
                  @blur="
                    form.ministry_focus =
                      form.ministry_focus_input
                        ?.split(',')
                        .map(s => s.trim())
                        .filter(Boolean) || []
                  "
                />
              </div>
            </div>
          </div>

          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-book-user size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.BACKSTORY') }}
            </h3>
            <div class="flex flex-col gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.BACKSTORY')
                }}</label>
                <textarea
                  v-model="form.backstory"
                  rows="3"
                  :placeholder="
                    t('MINISTRY.BOT_PERSONAS.FIELDS.BACKSTORY_PLACEHOLDER')
                  "
                  class="field-input resize-none"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.ORIGIN_STORY')
                }}</label>
                <textarea
                  v-model="form.origin_story"
                  rows="2"
                  :placeholder="
                    t('MINISTRY.BOT_PERSONAS.FIELDS.ORIGIN_STORY_PLACEHOLDER')
                  "
                  class="field-input resize-none"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- ── VOICE ──────────────────────────────────── -->
        <template v-else-if="activeTab === 'VOICE'">
          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-sliders-horizontal size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.TONE_STYLE') }}
            </h3>
            <div class="grid grid-cols-2 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.TONE')
                }}</label>
                <select v-model="form.tone" class="field-input">
                  <option v-for="tone in TONES" :key="tone" :value="tone">
                    {{ toneLabels[tone] }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.VOCABULARY_LEVEL')
                }}</label>
                <select v-model="form.vocabulary_level" class="field-input">
                  <option
                    v-for="lvl in VOCABULARY_LEVELS"
                    :key="lvl"
                    :value="lvl"
                  >
                    {{ vocabLabels[lvl] }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.SENTENCE_STRUCTURE')
                }}</label>
                <select v-model="form.sentence_structure" class="field-input">
                  <option v-for="s in SENTENCE_STRUCTURES" :key="s" :value="s">
                    {{ sentenceLabels[s] }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.FORMALITY_LEVEL')
                }}</label>
                <select v-model="form.formality_level" class="field-input">
                  <option v-for="f in FORMALITY_LEVELS" :key="f" :value="f">
                    {{ formalityLabels[f] }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.HUMOR_LEVEL')
                }}</label>
                <select v-model="form.humor_level" class="field-input">
                  <option v-for="h in HUMOR_LEVELS" :key="h" :value="h">
                    {{ humorLabels[h] }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.SCRIPTURE_STYLE')
                }}</label>
                <select v-model="form.scripture_style" class="field-input">
                  <option v-for="s in SCRIPTURE_STYLES" :key="s" :value="s">
                    {{ scriptureLabels[s] }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-1"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2 mb-3"
            >
              <span class="i-lucide-sparkles size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.EXPRESSION') }}
            </h3>
            <label
              class="flex items-center justify-between gap-3 cursor-pointer py-2.5 border-b border-[#F3F4F6]"
            >
              <span class="text-sm text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.USE_CONTRACTIONS')
              }}</span>
              <div class="relative flex-shrink-0 h-5 w-9">
                <input
                  v-model="form.use_contractions"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="absolute inset-0 rounded-full bg-[#D1D5DB] peer-checked:bg-woot-500 transition-colors"
                />
                <div
                  class="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4 pointer-events-none"
                />
              </div>
            </label>
            <label
              class="flex items-center justify-between gap-3 cursor-pointer py-2.5"
              :class="form.use_emojis ? 'border-b border-[#F3F4F6]' : ''"
            >
              <span class="text-sm text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.USE_EMOJIS')
              }}</span>
              <div class="relative flex-shrink-0 h-5 w-9">
                <input
                  v-model="form.use_emojis"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="absolute inset-0 rounded-full bg-[#D1D5DB] peer-checked:bg-woot-500 transition-colors"
                />
                <div
                  class="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4 pointer-events-none"
                />
              </div>
            </label>
            <div v-if="form.use_emojis" class="flex flex-col gap-1.5 pt-3">
              <label class="text-xs font-semibold text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.EMOJI_FREQUENCY')
              }}</label>
              <select v-model="form.emoji_frequency" class="field-input">
                <option v-for="e in EMOJI_FREQUENCIES" :key="e" :value="e">
                  {{ emojiFreqLabels[e] }}
                </option>
              </select>
            </div>
          </div>
        </template>

        <!-- ── MESSAGES ───────────────────────────────── -->
        <template v-else-if="activeTab === 'MESSAGES'">
          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-door-open size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.OPENING') }}
            </h3>
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.GREETING')
              }}</label>
              <input
                v-model="form.greeting"
                type="text"
                :placeholder="
                  t('MINISTRY.BOT_PERSONAS.FIELDS.GREETING_PLACEHOLDER')
                "
                class="field-input"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.OPENING_MESSAGE')
              }}</label>
              <textarea
                v-model="form.opening_message"
                rows="2"
                :placeholder="
                  t('MINISTRY.BOT_PERSONAS.FIELDS.OPENING_MESSAGE_PLACEHOLDER')
                "
                class="field-input resize-none"
              />
            </div>
          </div>

          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-reply size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.REPLIES') }}
            </h3>
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.FALLBACK_MESSAGE')
              }}</label>
              <textarea
                v-model="form.fallback_message"
                rows="2"
                :placeholder="
                  t('MINISTRY.BOT_PERSONAS.FIELDS.FALLBACK_MESSAGE_PLACEHOLDER')
                "
                class="field-input resize-none"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.RE_ENGAGEMENT_MESSAGE')
              }}</label>
              <textarea
                v-model="form.re_engagement_message"
                rows="2"
                :placeholder="
                  t(
                    'MINISTRY.BOT_PERSONAS.FIELDS.RE_ENGAGEMENT_MESSAGE_PLACEHOLDER'
                  )
                "
                class="field-input resize-none"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.PRAYER_OFFER_MESSAGE')
              }}</label>
              <input
                v-model="form.prayer_offer_message"
                type="text"
                :placeholder="
                  t(
                    'MINISTRY.BOT_PERSONAS.FIELDS.PRAYER_OFFER_MESSAGE_PLACEHOLDER'
                  )
                "
                class="field-input"
              />
            </div>
          </div>

          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-users size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.HANDOFF') }}
            </h3>
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.HANDOFF_MESSAGE')
              }}</label>
              <textarea
                v-model="form.handoff_message"
                rows="2"
                :placeholder="
                  t('MINISTRY.BOT_PERSONAS.FIELDS.HANDOFF_MESSAGE_PLACEHOLDER')
                "
                class="field-input resize-none"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.HUMAN_HANDOFF_MESSAGE')
              }}</label>
              <textarea
                v-model="form.human_handoff_message"
                rows="2"
                :placeholder="
                  t(
                    'MINISTRY.BOT_PERSONAS.FIELDS.HUMAN_HANDOFF_MESSAGE_PLACEHOLDER'
                  )
                "
                class="field-input resize-none"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.HANDOFF_SIGNAL')
              }}</label>
              <input
                v-model="form.handoff_signal"
                type="text"
                :placeholder="
                  t('MINISTRY.BOT_PERSONAS.FIELDS.HANDOFF_SIGNAL_PLACEHOLDER')
                "
                class="field-input"
              />
              <p class="text-xs text-[#9CA3AF]">
                {{ t('MINISTRY.BOT_PERSONAS.FIELDS.HANDOFF_SIGNAL_HINT') }}
              </p>
            </div>
          </div>
        </template>

        <!-- ── BEHAVIOR ───────────────────────────────── -->
        <template v-else-if="activeTab === 'BEHAVIOR'">
          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-text-cursor size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.RESPONSE_STYLE') }}
            </h3>
            <div class="flex flex-col gap-1.5 mb-1">
              <label class="text-xs font-semibold text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.RESPONSE_LENGTH')
              }}</label>
              <select v-model="form.response_length" class="field-input w-40">
                <option v-for="l in RESPONSE_LENGTHS" :key="l" :value="l">
                  {{ l }}
                </option>
              </select>
            </div>
            <div class="flex flex-col gap-1 border-t border-[#F3F4F6] pt-3">
              <label
                class="flex items-center justify-between gap-3 cursor-pointer py-2.5 border-b border-[#F3F4F6]"
              >
                <span class="text-sm text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.END_WITH_QUESTION')
                }}</span>
                <div class="relative flex-shrink-0 h-5 w-9">
                  <input
                    v-model="form.config.end_with_question"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="absolute inset-0 rounded-full bg-[#D1D5DB] peer-checked:bg-woot-500 transition-colors"
                  />
                  <div
                    class="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4 pointer-events-none"
                  />
                </div>
              </label>
              <label
                class="flex items-center justify-between gap-3 cursor-pointer py-2.5 border-b border-[#F3F4F6]"
              >
                <span class="text-sm text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.INCLUDE_VERSES')
                }}</span>
                <div class="relative flex-shrink-0 h-5 w-9">
                  <input
                    v-model="form.config.include_verses"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="absolute inset-0 rounded-full bg-[#D1D5DB] peer-checked:bg-woot-500 transition-colors"
                  />
                  <div
                    class="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4 pointer-events-none"
                  />
                </div>
              </label>
              <label
                class="flex items-center justify-between gap-3 cursor-pointer py-2.5"
                :class="
                  form.enable_chunked_replies ? 'border-b border-[#F3F4F6]' : ''
                "
              >
                <span class="text-sm text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.CHUNKED_REPLIES')
                }}</span>
                <div class="relative flex-shrink-0 h-5 w-9">
                  <input
                    v-model="form.enable_chunked_replies"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="absolute inset-0 rounded-full bg-[#D1D5DB] peer-checked:bg-woot-500 transition-colors"
                  />
                  <div
                    class="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4 pointer-events-none"
                  />
                </div>
              </label>
              <div
                v-if="form.enable_chunked_replies"
                class="flex flex-col gap-1.5 pt-3"
              >
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.MAX_CHUNKS')
                }}</label>
                <input
                  v-model.number="form.max_chunks_per_reply"
                  type="number"
                  min="2"
                  max="5"
                  class="field-input w-24"
                />
              </div>
            </div>
          </div>

          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-timer size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.TIMING') }}
            </h3>
            <div class="grid grid-cols-2 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.TYPING_SPEED_WPM')
                }}</label>
                <input
                  v-model.number="form.typing_speed_wpm"
                  type="number"
                  min="10"
                  max="200"
                  class="field-input"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.DEBOUNCE_SECONDS')
                }}</label>
                <input
                  v-model.number="form.config.debounce_seconds"
                  type="number"
                  min="0"
                  max="120"
                  class="field-input"
                />
                <p class="text-xs text-[#9CA3AF]">
                  {{ t('MINISTRY.BOT_PERSONAS.FIELDS.DEBOUNCE_SECONDS_HINT') }}
                </p>
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.MIN_REPLY_DELAY')
                }}</label>
                <input
                  v-model.number="form.min_reply_delay_secs"
                  type="number"
                  min="0"
                  max="10"
                  step="0.5"
                  class="field-input"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.MAX_REPLY_DELAY')
                }}</label>
                <input
                  v-model.number="form.max_reply_delay_secs"
                  type="number"
                  min="0.5"
                  max="30"
                  step="0.5"
                  class="field-input"
                />
              </div>
            </div>
          </div>

          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-gauge size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.LIMITS') }}
            </h3>
            <div class="grid grid-cols-3 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.MAX_CONVERSATION_TURNS')
                }}</label>
                <input
                  v-model.number="form.max_conversation_turns"
                  type="number"
                  min="1"
                  max="100"
                  class="field-input"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.AUTO_ESCALATE_AFTER_TURNS')
                }}</label>
                <input
                  v-model.number="form.auto_escalate_after_turns"
                  type="number"
                  min="1"
                  max="100"
                  class="field-input"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.MAX_BOT_MESSAGES')
                }}</label>
                <input
                  v-model.number="form.config.max_bot_messages"
                  type="number"
                  min="0"
                  :placeholder="
                    t(
                      'MINISTRY.BOT_PERSONAS.FIELDS.MAX_BOT_MESSAGES_PLACEHOLDER'
                    )
                  "
                  class="field-input"
                />
                <p class="text-xs text-[#9CA3AF]">
                  {{ t('MINISTRY.BOT_PERSONAS.FIELDS.MAX_BOT_MESSAGES_HINT') }}
                </p>
              </div>
            </div>
          </div>
        </template>

        <!-- ── EMOTIONAL ──────────────────────────────── -->
        <template v-else-if="activeTab === 'EMOTIONAL'">
          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-heart size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.EMOTION_SETTINGS') }}
            </h3>
            <div class="grid grid-cols-2 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.EMPATHY_LEVEL')
                }}</label>
                <select v-model="form.empathy_level" class="field-input">
                  <option v-for="e in EMPATHY_LEVELS" :key="e" :value="e">
                    {{ empathyLabels[e] }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.JOY_RESPONSE_STYLE')
                }}</label>
                <select v-model="form.joy_response_style" class="field-input">
                  <option v-for="j in JOY_RESPONSE_STYLES" :key="j" :value="j">
                    {{ joyLabels[j] }}
                  </option>
                </select>
              </div>
            </div>
            <div class="flex flex-col gap-1 border-t border-[#F3F4F6] pt-3">
              <label
                class="flex items-center justify-between gap-3 cursor-pointer py-2.5 border-b border-[#F3F4F6]"
              >
                <span class="text-sm text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.CRISIS_SENSITIVITY')
                }}</span>
                <div class="relative flex-shrink-0 h-5 w-9">
                  <input
                    v-model="form.crisis_sensitivity"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="absolute inset-0 rounded-full bg-[#D1D5DB] peer-checked:bg-woot-500 transition-colors"
                  />
                  <div
                    class="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4 pointer-events-none"
                  />
                </div>
              </label>
              <label
                class="flex items-center justify-between gap-3 cursor-pointer py-2.5"
              >
                <span class="text-sm text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.ALWAYS_AFFIRM')
                }}</span>
                <div class="relative flex-shrink-0 h-5 w-9">
                  <input
                    v-model="form.always_affirm_before_correcting"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="absolute inset-0 rounded-full bg-[#D1D5DB] peer-checked:bg-woot-500 transition-colors"
                  />
                  <div
                    class="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4 pointer-events-none"
                  />
                </div>
              </label>
            </div>
          </div>

          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-activity size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.PROTOCOLS') }}
            </h3>
            <div class="flex flex-col gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.GRIEF_PROTOCOL')
                }}</label>
                <textarea
                  v-model="form.grief_protocol"
                  rows="2"
                  :placeholder="
                    t('MINISTRY.BOT_PERSONAS.FIELDS.GRIEF_PROTOCOL_PLACEHOLDER')
                  "
                  class="field-input resize-none"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.DOUBT_PROTOCOL')
                }}</label>
                <textarea
                  v-model="form.doubt_protocol"
                  rows="2"
                  :placeholder="
                    t('MINISTRY.BOT_PERSONAS.FIELDS.DOUBT_PROTOCOL_PLACEHOLDER')
                  "
                  class="field-input resize-none"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.ANGER_PROTOCOL')
                }}</label>
                <textarea
                  v-model="form.anger_protocol"
                  rows="2"
                  :placeholder="
                    t('MINISTRY.BOT_PERSONAS.FIELDS.ANGER_PROTOCOL_PLACEHOLDER')
                  "
                  class="field-input resize-none"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- ── CULTURAL ───────────────────────────────── -->
        <template v-else-if="activeTab === 'CULTURAL'">
          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-1"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2 mb-3"
            >
              <span class="i-lucide-globe size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.LANGUAGE') }}
            </h3>
            <label
              class="flex items-center justify-between gap-3 cursor-pointer py-2.5 border-b border-[#F3F4F6]"
            >
              <span class="text-sm text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.AUTO_DETECT_LANGUAGE')
              }}</span>
              <div class="relative flex-shrink-0 h-5 w-9">
                <input
                  v-model="form.auto_detect_language"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="absolute inset-0 rounded-full bg-[#D1D5DB] peer-checked:bg-woot-500 transition-colors"
                />
                <div
                  class="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4 pointer-events-none"
                />
              </div>
            </label>
            <label
              class="flex items-center justify-between gap-3 cursor-pointer py-2.5 border-b border-[#F3F4F6]"
            >
              <span class="text-sm text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.AVOID_WESTERN_IDIOMS')
              }}</span>
              <div class="relative flex-shrink-0 h-5 w-9">
                <input
                  v-model="form.avoid_western_idioms"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="absolute inset-0 rounded-full bg-[#D1D5DB] peer-checked:bg-woot-500 transition-colors"
                />
                <div
                  class="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4 pointer-events-none"
                />
              </div>
            </label>
            <div class="flex flex-col gap-1.5 pt-3">
              <label class="text-xs font-semibold text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.CULTURAL_SENSITIVITY_NOTES')
              }}</label>
              <textarea
                v-model="form.cultural_sensitivity_notes"
                rows="3"
                :placeholder="
                  t(
                    'MINISTRY.BOT_PERSONAS.FIELDS.CULTURAL_SENSITIVITY_NOTES_PLACEHOLDER'
                  )
                "
                class="field-input resize-none"
              />
            </div>
          </div>
        </template>

        <!-- ── RULES ──────────────────────────────────── -->
        <template v-else-if="activeTab === 'RULES'">
          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-1"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2 mb-3"
            >
              <span class="i-lucide-shield size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.CONV_RULES') }}
            </h3>
            <label
              class="flex items-center justify-between gap-3 cursor-pointer py-2.5 border-b border-[#F3F4F6]"
            >
              <span class="text-sm text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.NEVER_DEBATE')
              }}</span>
              <div class="relative flex-shrink-0 h-5 w-9">
                <input
                  v-model="form.never_debate_mode"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="absolute inset-0 rounded-full bg-[#D1D5DB] peer-checked:bg-woot-500 transition-colors"
                />
                <div
                  class="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4 pointer-events-none"
                />
              </div>
            </label>
            <div class="flex flex-col gap-4 pt-3">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.FORBIDDEN_TOPICS')
                }}</label>
                <textarea
                  :value="(form.forbidden_topics || []).join(', ')"
                  rows="2"
                  :placeholder="
                    t(
                      'MINISTRY.BOT_PERSONAS.FIELDS.FORBIDDEN_TOPICS_PLACEHOLDER'
                    )
                  "
                  class="field-input resize-none"
                  @blur="
                    e =>
                      (form.forbidden_topics = e.target.value
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean))
                  "
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.REQUIRED_DISCLAIMERS')
                }}</label>
                <textarea
                  :value="(form.required_disclaimers || []).join(', ')"
                  rows="2"
                  :placeholder="
                    t(
                      'MINISTRY.BOT_PERSONAS.FIELDS.REQUIRED_DISCLAIMERS_PLACEHOLDER'
                    )
                  "
                  class="field-input resize-none"
                  @blur="
                    e =>
                      (form.required_disclaimers = e.target.value
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean))
                  "
                />
              </div>
            </div>
          </div>

          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-shield-check size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.FIELDS.IDENTITY_PROTECTION_TITLE') }}
            </h3>
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.DEFLECTION_STYLE')
              }}</label>
              <select v-model="form.deflection_style" class="field-input">
                <option
                  v-for="style in DEFLECTION_STYLES"
                  :key="style"
                  :value="style"
                >
                  {{ deflectionStyleLabels[style] }}
                </option>
              </select>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.CHARACTER_LOCATION')
                }}</label>
                <input
                  v-model="form.character_location"
                  type="text"
                  class="field-input"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.CHARACTER_AGE')
                }}</label>
                <input
                  v-model.number="form.character_age"
                  type="number"
                  min="18"
                  max="99"
                  class="field-input"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.MINISTRY_ROLE')
                }}</label>
                <input
                  v-model="form.ministry_role"
                  type="text"
                  class="field-input"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.FAVOURITE_FOOD')
                }}</label>
                <input
                  v-model="form.favourite_food"
                  type="text"
                  class="field-input"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.HOBBY')
                }}</label>
                <input v-model="form.hobby" type="text" class="field-input" />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.FAVOURITE_BOOK')
                }}</label>
                <input
                  v-model="form.favourite_book"
                  type="text"
                  class="field-input"
                />
              </div>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.CUSTOM_DEFLECTIONS')
              }}</label>
              <div
                v-for="(phrase, idx) in form.custom_deflections"
                :key="idx"
                class="flex items-center gap-2 text-sm text-[#374151] py-1.5 border-b border-[#F3F4F6]"
              >
                <span class="i-lucide-chevron-right size-3 text-[#9CA3AF]" />
                <span class="flex-1 truncate">{{ phrase }}</span>
                <button
                  :aria-label="t('MINISTRY.BOT_PERSONAS.REMOVE')"
                  class="text-ruby-500 hover:text-ruby-700"
                  @click="removeCustomDeflection(idx)"
                >
                  <span class="i-lucide-x size-3" />
                </button>
              </div>
              <div class="flex gap-2 mt-1">
                <input
                  v-model="newCustomDeflection"
                  type="text"
                  :placeholder="
                    t(
                      'MINISTRY.BOT_PERSONAS.FIELDS.CUSTOM_DEFLECTION_PLACEHOLDER'
                    )
                  "
                  class="field-input flex-1"
                  @keydown.enter.prevent="addCustomDeflection"
                />
                <button
                  class="px-3 py-2 rounded-lg bg-woot-500 text-white text-sm hover:bg-woot-600 flex-shrink-0"
                  @click="addCustomDeflection"
                >
                  {{ t('MINISTRY.BOT_PERSONAS.FIELDS.ADD_DEFLECTION') }}
                </button>
              </div>
            </div>
            <label
              class="flex items-center justify-between gap-3 cursor-pointer py-2.5 border-t border-[#F3F4F6]"
            >
              <span class="text-sm text-[#374151]">{{
                t('MINISTRY.BOT_PERSONAS.FIELDS.ALERT_ON_PROBE')
              }}</span>
              <div class="relative flex-shrink-0 h-5 w-9">
                <input
                  v-model="form.alert_on_probe"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="absolute inset-0 rounded-full bg-[#D1D5DB] peer-checked:bg-woot-500 transition-colors"
                />
                <div
                  class="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4 pointer-events-none"
                />
              </div>
            </label>
          </div>
        </template>

        <!-- ── AI CONFIG ──────────────────────────────── -->
        <template v-else-if="activeTab === 'AI_CONFIG'">
          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-list-plus size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.INSTRUCTIONS.TITLE') }}
            </h3>
            <div class="flex flex-col gap-1">
              <div
                v-for="(inst, idx) in instructions"
                :key="idx"
                class="flex items-center gap-2 py-2 border-b border-[#F3F4F6]"
              >
                <span
                  class="text-xs px-2 py-0.5 rounded-full bg-woot-50 text-woot-700 font-medium capitalize flex-shrink-0"
                  >{{ inst.category }}</span
                >
                <span class="flex-1 text-sm text-[#374151] truncate">{{
                  inst.instruction
                }}</span>
                <button
                  class="text-ruby-500 hover:text-ruby-700 flex-shrink-0"
                  :aria-label="t('MINISTRY.BOT_PERSONAS.REMOVE')"
                  @click="removeInstruction(idx)"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
              </div>
            </div>
            <div class="flex gap-2">
              <select
                v-model="newInstruction.category"
                class="field-input w-32"
              >
                <option
                  v-for="cat in INSTRUCTION_CATEGORIES"
                  :key="cat"
                  :value="cat"
                >
                  {{ cat }}
                </option>
              </select>
              <input
                v-model="newInstruction.instruction"
                type="text"
                :placeholder="
                  t(
                    'MINISTRY.BOT_PERSONAS.INSTRUCTIONS.INSTRUCTION_PLACEHOLDER'
                  )
                "
                class="field-input flex-1"
              />
              <button
                class="px-3 py-2 rounded-lg bg-woot-500 text-white text-sm hover:bg-woot-600 flex-shrink-0"
                @click="addInstruction"
              >
                {{ t('MINISTRY.BOT_PERSONAS.INSTRUCTIONS.ADD') }}
              </button>
            </div>
          </div>

          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-graduation-cap size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.EXPERTISES.TITLE') }}
            </h3>
            <div class="flex flex-col gap-1">
              <div
                v-for="(exp, idx) in expertises"
                :key="idx"
                class="flex items-center gap-2 py-2 border-b border-[#F3F4F6]"
              >
                <span class="flex-1 text-sm text-[#374151]">{{
                  exp.topic
                }}</span>
                <span
                  class="text-xs px-2 py-0.5 rounded-full bg-n-slate-3 text-n-slate-11 capitalize flex-shrink-0"
                  >{{ exp.level }}</span
                >
                <button
                  class="text-ruby-500 hover:text-ruby-700 flex-shrink-0"
                  :aria-label="t('MINISTRY.BOT_PERSONAS.REMOVE')"
                  @click="removeExpertise(idx)"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
              </div>
            </div>
            <div class="flex gap-2">
              <input
                v-model="newExpertise.topic"
                type="text"
                :placeholder="
                  t('MINISTRY.BOT_PERSONAS.EXPERTISES.TOPIC_PLACEHOLDER')
                "
                class="field-input flex-1"
              />
              <select v-model="newExpertise.level" class="field-input w-32">
                <option v-for="lvl in EXPERTISE_LEVELS" :key="lvl" :value="lvl">
                  {{ lvl }}
                </option>
              </select>
              <button
                class="px-3 py-2 rounded-lg bg-woot-500 text-white text-sm hover:bg-woot-600 flex-shrink-0"
                @click="addExpertise"
              >
                {{ t('MINISTRY.BOT_PERSONAS.EXPERTISES.ADD') }}
              </button>
            </div>
          </div>

          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-quote size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.PHRASES.TITLE') }}
            </h3>
            <div class="flex flex-col gap-1">
              <div
                v-for="(ph, idx) in phrases"
                :key="idx"
                class="flex items-center gap-2 py-2 border-b border-[#F3F4F6]"
              >
                <span
                  class="text-xs px-2 py-0.5 rounded-full bg-n-slate-3 text-n-slate-11 capitalize flex-shrink-0"
                  >{{ ph.phrase_type }}</span
                >
                <span class="flex-1 text-sm text-[#374151] truncate">{{
                  ph.phrase
                }}</span>
                <button
                  class="text-ruby-500 hover:text-ruby-700 flex-shrink-0"
                  :aria-label="t('MINISTRY.BOT_PERSONAS.REMOVE')"
                  @click="removePhrase(idx)"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
              </div>
            </div>
            <div class="flex gap-2">
              <select v-model="newPhrase.phrase_type" class="field-input w-32">
                <option v-for="pt in PHRASE_TYPES" :key="pt" :value="pt">
                  {{ pt }}
                </option>
              </select>
              <input
                v-model="newPhrase.phrase"
                type="text"
                :placeholder="
                  t('MINISTRY.BOT_PERSONAS.PHRASES.PHRASE_PLACEHOLDER')
                "
                class="field-input flex-1"
              />
              <button
                class="px-3 py-2 rounded-lg bg-woot-500 text-white text-sm hover:bg-woot-600 flex-shrink-0"
                @click="addPhrase"
              >
                {{ t('MINISTRY.BOT_PERSONAS.PHRASES.ADD') }}
              </button>
            </div>
          </div>

          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-cpu size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.SECTIONS.AI_TUNING') }}
            </h3>
            <div class="grid grid-cols-2 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.TEMPERATURE_OVERRIDE')
                }}</label>
                <input
                  v-model.number="form.temperature_override"
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  :placeholder="
                    t(
                      'MINISTRY.BOT_PERSONAS.FIELDS.TEMPERATURE_OVERRIDE_PLACEHOLDER'
                    )
                  "
                  class="field-input"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.MODEL_OVERRIDE')
                }}</label>
                <input
                  v-model="form.model_override"
                  type="text"
                  :placeholder="
                    t('MINISTRY.BOT_PERSONAS.FIELDS.MODEL_OVERRIDE_PLACEHOLDER')
                  "
                  class="field-input"
                />
              </div>
              <div class="col-span-2 flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.FIELDS.SYSTEM_PROMPT_OVERRIDE')
                }}</label>
                <textarea
                  v-model="form.system_prompt_override"
                  rows="5"
                  :placeholder="
                    t(
                      'MINISTRY.BOT_PERSONAS.FIELDS.SYSTEM_PROMPT_OVERRIDE_PLACEHOLDER'
                    )
                  "
                  class="field-input resize-none font-mono text-xs"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- ── KNOWLEDGE ──────────────────────────────── -->
        <template v-else-if="activeTab === 'KNOWLEDGE'">
          <div
            class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-4"
          >
            <h3
              class="text-sm font-semibold text-[#1A1A2E] flex items-center gap-2"
            >
              <span class="i-lucide-book-open size-4 text-woot-500" />
              {{ t('MINISTRY.BOT_PERSONAS.KB_ENTRIES.TITLE') }}
            </h3>
            <p class="text-sm text-[#6B7280] -mt-2">
              {{ t('MINISTRY.BOT_PERSONAS.KB_ENTRIES.DESCRIPTION') }}
            </p>

            <div
              v-for="(entry, idx) in kbEntries"
              :key="idx"
              class="rounded-lg border border-[#E8E6E0] p-3.5 flex flex-col gap-2"
            >
              <div class="flex items-center gap-2">
                <span
                  class="text-xs px-2 py-0.5 rounded-full bg-woot-50 text-woot-700 capitalize font-medium flex-shrink-0"
                  >{{ entry.category }}</span
                >
                <span
                  v-if="entry.title"
                  class="flex-1 text-sm font-semibold text-[#1A1A2E] truncate"
                  >{{ entry.title }}</span
                >
                <button
                  class="text-ruby-500 hover:text-ruby-700 ml-auto flex-shrink-0"
                  :aria-label="t('MINISTRY.BOT_PERSONAS.REMOVE')"
                  @click="removeKbEntry(idx)"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </div>
              <p class="text-sm text-[#6B7280]">
                <span class="font-semibold text-[#374151]">{{
                  t('MINISTRY.BOT_PERSONAS.KB_ENTRIES.Q_LABEL')
                }}</span>
                {{ entry.question }}
              </p>
              <p class="text-sm text-[#374151]">
                <span class="font-semibold">{{
                  t('MINISTRY.BOT_PERSONAS.KB_ENTRIES.A_LABEL')
                }}</span>
                {{ entry.answer }}
              </p>
            </div>

            <div class="flex flex-col gap-3 pt-2 border-t border-[#E8E6E0]">
              <div class="flex gap-3">
                <select v-model="newKbEntry.category" class="field-input w-36">
                  <option
                    v-for="cat in KB_CATEGORIES"
                    :key="cat"
                    :value="cat"
                    class="capitalize"
                  >
                    {{ cat }}
                  </option>
                </select>
                <input
                  v-model="newKbEntry.title"
                  type="text"
                  :placeholder="
                    t('MINISTRY.BOT_PERSONAS.KB_ENTRIES.TITLE_PLACEHOLDER')
                  "
                  class="field-input flex-1"
                />
              </div>
              <textarea
                v-model="newKbEntry.question"
                rows="2"
                :placeholder="
                  t('MINISTRY.BOT_PERSONAS.KB_ENTRIES.QUESTION_PLACEHOLDER')
                "
                class="field-input resize-none"
              />
              <textarea
                v-model="newKbEntry.answer"
                rows="3"
                :placeholder="
                  t('MINISTRY.BOT_PERSONAS.KB_ENTRIES.ANSWER_PLACEHOLDER')
                "
                class="field-input resize-none"
              />
              <button
                class="self-end px-4 py-2 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600"
                @click="addKbEntry"
              >
                {{ t('MINISTRY.BOT_PERSONAS.KB_ENTRIES.ADD') }}
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.field-input {
  @apply w-full rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-400 bg-transparent;
}
</style>
